import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Container, FormHelperText, Grid, InputLabel, TextField } from '@material-ui/core';
import { Spinner } from '@danfoss/webex-ui';
import queryString from 'query-string';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Footer from '../Footer/Footer';
import LogoAndBackground from '../Header/LogoAndBackground';
import Information from '../Information/Information';
import { useSharedStyles } from '../../sharedStyles/Styles';
import { validateEmailChangeToken, submitNewEmail } from '../../api/email';
import { ApiError, ApiErrorCode } from '../../api/errors';
import { InvalidLinkContent } from '../EmailChangeConfirmation/InvalidLinkContent';
import styles from './CurrentEmailVerification.module.scss';


enum PageState {
  Loading,
  Invalid,
  EnterNew,
  Sent,
};

const getToken = (): string => {
  const urlParams = queryString.parse(globalThis.location.search);
  return DOMPurify.sanitize(urlParams.token as string);
};

const CurrentEmailVerification: React.FC = () => {
  const { t, i18n } = useTranslation();
  const sharedClasses = useSharedStyles();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>(PageState.Loading);
  const [token, setToken] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const emailSchema = useMemo(() => z.object({
    email: z.email(t('login_signup.email.validation.valid_email')),
  }), [t]);

  const {
    register,
    handleSubmit: hookHandleSubmit,
    formState: { isValid, isSubmitting, errors },
  } = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange',
  });

  const emailField = register('email');

  useEffect(() => {
    const validateToken = async () => {
      const rawToken = getToken();
      if (!rawToken) {
        navigate('/error?error_description=Invalid query params', { replace: true });
        return;
      }

      try {
        const isValid = await validateEmailChangeToken(rawToken);
        if (isValid) {
          setToken(rawToken);
          setPageState(PageState.EnterNew);
        } else {
          setPageState(PageState.Invalid);
        }
      } catch {
        navigate('/error?error_description=Error validating token', { replace: true });
      }
    };

    validateToken();
  }, [navigate]);

  const handleSubmit = async (data: z.infer<typeof emailSchema>) => {
    setFieldError(null);

    try {
      await submitNewEmail(data.email, i18n.language, token);
      setSubmittedEmail(data.email);
      setPageState(PageState.Sent);
    } catch (err) {
      const errorCodeToKey: Partial<Record<ApiErrorCode, string>> = {
        [ApiErrorCode.EmailAlreadyUsed]: 'current_email_verification.enter.email_in_use_error',
        [ApiErrorCode.ValidationError]: 'current_email_verification.enter.validation_error',
        [ApiErrorCode.FederatedEmailDomain]: 'current_email_verification.enter.federated_email_domain_error',
      };
      const apiError = err as ApiError;
      const translationKey = errorCodeToKey[apiError?.error_code] ?? 'current_email_verification.enter.generic_error';
      setFieldError(t(translationKey));
    }
  };

  if (pageState === PageState.Loading) {
    return <Spinner visible />;
  }

  const renderInvalid = () => <InvalidLinkContent />;

  const renderSent = () => (
    <Information
      headerText={t('current_email_verification.sent.header')}
      bodyText={(
        <Grid container spacing={1}>
          <Grid item xs={12} className={styles.bodyText}>
            {parse(t('user_profile.change_email.description_verify', { email: submittedEmail }))}
          </Grid>
        </Grid>
      )}
    />
  );

  const renderEnterNew = () => (
    <form onSubmit={hookHandleSubmit(handleSubmit)}>
      <Information
        headerText={t('current_email_verification.enter.header')}
        bodyText={(
          <Grid container spacing={2}>
            <Grid item xs={12} className={styles.bodyText}>
              {parse(t('current_email_verification.enter.body'))}
            </Grid>
            <Grid item xs={12}>
              <InputLabel shrink required htmlFor='new-email'>
                {t('current_email_verification.enter.email_label')}
              </InputLabel>
              <TextField
                id='new-email'
                type='email'
                fullWidth
                error={!!fieldError || !!errors.email}
                inputProps={{ 'data-testid': 'new-email-field', className: styles.emailInput }}
                {...emailField}
                onChange={(event) => {
                  if (fieldError) {
                    setFieldError(null);
                  }

                  emailField.onChange(event);
                }}
              />
              {(fieldError || errors.email) && (
                <FormHelperText error data-testid='email-error'>
                  {fieldError ?? errors.email!.message!}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        )}
        buttonText={t('current_email_verification.enter.button')}
        buttonProps={{
          disabled: !isValid || isSubmitting,
          startIcon: isSubmitting && <CircularProgress size={16} color='inherit' />,
        }}
      />
    </form>
  );

  const screenRenderers: Record<PageState, () => React.ReactNode> = {
    [PageState.Loading]: () => null,
    [PageState.Invalid]: renderInvalid,
    [PageState.EnterNew]: renderEnterNew,
    [PageState.Sent]: renderSent,
  };

  return (
    <>
      <LogoAndBackground classes={sharedClasses} />
      <Container className={sharedClasses.containerRoot}>
        <Container maxWidth='md' className={`${sharedClasses.outerContainer} ${sharedClasses.relativeContainer}`}>
          <Grid container className={sharedClasses.contentContainerRoot}>
            <Grid item xs={12}>
              {screenRenderers[pageState]()}
            </Grid>
          </Grid>
        </Container>
      </Container>
      <Footer contentClass={sharedClasses.footerContent} />
    </>
  );
};

export default CurrentEmailVerification;
